import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaPlus, FaEdit, FaTrash, FaUpload, FaBook, FaRoute, 
  FaClock, FaChartLine, FaPlay,
  FaVideo, FaFilePdf, FaCode, FaFileArchive
} from 'react-icons/fa';
import './Training.css';
import apiClient from '../apiClient';
import { useAuth } from '../contexts/AuthContext';
import ScormViewer from '../components/training/ScormViewer';

// Backend URL for direct file access and SCORM launch
const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://changemanagement-backend-ahfre3hvgcerbrgp.canadacentral-01.azurewebsites.net'
    : 'http://localhost:5000'
  );

const Training = () => {
  const { user } = useAuth(); // Add user authentication
  
  // State management
  const [activeTab, setActiveTab] = useState('modules');
  const [modules, setModules] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showPathModal, setShowPathModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showScormViewer, setShowScormViewer] = useState(false);
  const [scormModule, setScormModule] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingPath, setEditingPath] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    contentType: '',
    difficulty: '',
    tagType: '',
    tagValue: '',
    projectId: ''
  });
  
  // Form states
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    content_type: 'video_embed',
    difficulty_level: 'beginner',
    content_url: '',
    content_data: '',
    duration_minutes: '',
    is_mandatory: false,
    tags: [],
    prerequisites: []
  });
  
  const [pathForm, setPathForm] = useState({
    name: '',
    description: '',
    target_role: '',
    target_department: '',
    change_impact_level: '',
    project_id: '',
    estimated_duration_hours: '',
    modules: []
  });

  // User role detection
  const userRole = user?.role;
  const isAdmin = userRole === 'Admin' || userRole === 'System Administrator';
  const isChangeManager = userRole === 'Change Manager';
  const canManage = isAdmin || isChangeManager;

  // Content type icons
  const getContentTypeIcon = (type) => {
    const icons = {
      video_embed: <FaVideo />,
      video_link: <FaVideo />,
      pdf: <FaFilePdf />,
      html5: <FaCode />,
      scorm: <FaFileArchive />,
      document: <FaFilePdf />,
      interactive: <FaCode />
    };
    return icons[type] || <FaBook />;
  };

  // Load data functions with useCallback to fix dependency issues
  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await apiClient.get(`/training/modules?${params}`);
      setModules(response.data.modules || []);
    } catch (err) {
      setError('Failed to load training modules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadLearningPaths = useCallback(async () => {
    try {
      const response = await apiClient.get('/training/learning-paths');
      setLearningPaths(response.data.learning_paths || []);
    } catch (err) {
      setError('Failed to load learning paths');
      console.error(err);
    }
  }, []);

  const loadUserProgress = useCallback(async () => {
    try {
      const response = await apiClient.get('/training/user/progress');
      setUserProgress(response.data.completions || []);
    } catch (err) {
      console.error('Failed to load user progress:', err);
    }
  }, []);

  // Load data
  useEffect(() => {
    loadModules();
    loadLearningPaths();
    loadUserProgress();
  }, [loadModules, loadLearningPaths, loadUserProgress]);

  // Module management
  const handleCreateModule = () => {
    setEditingModule(null);
    setModuleForm({
      title: '',
      description: '',
      content_type: 'video_embed',
      difficulty_level: 'beginner',
      content_url: '',
      content_data: '',
      duration_minutes: '',
      is_mandatory: false,
      tags: [],
      prerequisites: []
    });
    setShowModuleModal(true);
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title,
      description: module.description,
      content_type: module.content_type,
      difficulty_level: module.difficulty_level,
      content_url: module.content_url || '',
      content_data: module.content_data || '',
      duration_minutes: module.duration_minutes || '',
      is_mandatory: module.is_mandatory,
      tags: module.tags || [],
      prerequisites: module.prerequisites || []
    });
    setShowModuleModal(true);
  };

  const handleSaveModule = async () => {
    try {
      setLoading(true);
      
      if (editingModule) {
        await apiClient.put(`/training/modules/${editingModule.id}`, moduleForm);
      } else {
        await apiClient.post('/training/modules', moduleForm);
      }
      
      setShowModuleModal(false);
      loadModules();
    } catch (err) {
      setError('Failed to save module');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    
    try {
      await apiClient.delete(`/training/modules/${moduleId}`);
      loadModules();
    } catch (err) {
      setError('Failed to delete module');
      console.error(err);
    }
  };

  // Learning path management
  const handleCreatePath = () => {
    setEditingPath(null);
    setPathForm({
      name: '',
      description: '',
      target_role: '',
      target_department: '',
      change_impact_level: '',
      project_id: '',
      estimated_duration_hours: '',
      modules: []
    });
    setShowPathModal(true);
  };

  const handleEditPath = (path) => {
    setEditingPath(path);
    setPathForm({
      name: path.name,
      description: path.description,
      target_role: path.target_role || '',
      target_department: path.target_department || '',
      change_impact_level: path.change_impact_level || '',
      project_id: path.project_id || '',
      estimated_duration_hours: path.estimated_duration_hours || '',
      modules: path.items?.map(item => ({
        module_id: item.module_id,
        is_optional: item.is_optional
      })) || []
    });
    setShowPathModal(true);
  };

  const handleSavePath = async () => {
    try {
      setLoading(true);
      
      if (editingPath) {
        await apiClient.put(`/training/learning-paths/${editingPath.id}`, pathForm);
      } else {
        await apiClient.post('/training/learning-paths', pathForm);
      }
      
      setShowPathModal(false);
      loadLearningPaths();
    } catch (err) {
      setError('Failed to save learning path');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePath = async (pathId) => {
    if (!window.confirm('Are you sure you want to delete this learning path?')) return;
    
    try {
      await apiClient.delete(`/training/learning-paths/${pathId}`);
      loadLearningPaths();
    } catch (err) {
      setError('Failed to delete learning path');
      console.error(err);
    }
  };

  // File upload
  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/training/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update module form with file info
      const updateData = {
        file_path: response.data.file_path,
        content_type: response.data.content_type
      };
      
      // If it's a SCORM package, save additional SCORM data
      if (response.data.content_type === 'scorm' && response.data.scorm_id) {
        updateData.content_data = JSON.stringify({
          scorm_path: response.data.scorm_path,
          scorm_id: response.data.scorm_id,
          launch_file: response.data.launch_file
        });
      }
      
      setModuleForm(prev => ({
        ...prev,
        ...updateData
      }));
      
      setShowUploadModal(false);
    } catch (err) {
      setError('Failed to upload file');
      console.error(err);
    }
  };

  // Add tag to module
  const addTag = () => {
    setModuleForm(prev => ({
      ...prev,
      tags: [...prev.tags, { tag_type: 'project', tag_value: '' }]
    }));
  };

  const updateTag = (index, field, value) => {
    setModuleForm(prev => ({
      ...prev,
      tags: prev.tags.map((tag, i) => 
        i === index ? { ...tag, [field]: value } : tag
      )
    }));
  };

  const removeTag = (index) => {
    setModuleForm(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  // Handle starting a training module (for learners)
  const handleStartModule = async (module) => {
    try {
      // Record that the user started this module
      await apiClient.post(`/training/modules/${module.id}/start`);
      
      // Launch the module based on its content type
      if (module.content_type === 'scorm') {
        // Open SCORM content in custom viewer instead of new window
        setScormModule(module);
        setShowScormViewer(true);
      } else if (module.content_type === 'video_embed' || module.content_type === 'video_link') {
        // Open video content
        if (module.content_url) {
          window.open(module.content_url, '_blank');
        }
      } else if (module.content_type === 'pdf' || module.content_type === 'document') {
        // Open document in new tab using absolute backend URL
        if (module.file_path) {
          const token = localStorage.getItem('access_token');
          window.open(`${BACKEND_BASE_URL}/api/training/files/${module.file_path}${token ? `?token=${token}` : ''}`, '_blank');
        } else if (module.content_url) {
          window.open(module.content_url, '_blank');
        }
      } else {
        // For other content types, open URL or use absolute backend URL
        const url = module.content_url || `${BACKEND_BASE_URL}/api/training/modules/${module.id}/launch`;
        window.open(url, '_blank');
      }
      
      // Refresh progress data
      loadUserProgress();
    } catch (err) {
      setError('Failed to start module');
      console.error(err);
    }
  };

  const handleScormComplete = (module) => {
    // Module completed, refresh progress
    loadUserProgress();
    loadModules(); // Refresh modules to show updated progress
  };

  return (
    <div className="training-dashboard">
      <div className="page-header">
        <h1>Training Framework</h1>
        <div className="header-actions">
          <button 
            className="btn-primary"
            onClick={handleCreateModule}
          >
            <FaPlus /> New Module
          </button>
          <button 
            className="btn-secondary"
            onClick={handleCreatePath}
          >
            <FaRoute /> New Learning Path
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="tab-navigation">
        <button 
          className={`tab ${activeTab === 'modules' ? 'active' : ''}`}
          onClick={() => setActiveTab('modules')}
        >
          <FaBook /> Training Modules
        </button>
        <button 
          className={`tab ${activeTab === 'paths' ? 'active' : ''}`}
          onClick={() => setActiveTab('paths')}
        >
          <FaRoute /> Learning Paths
        </button>
        <button 
          className={`tab ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          <FaChartLine /> Progress Tracking
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <select 
            value={filters.contentType}
            onChange={(e) => setFilters(prev => ({ ...prev, contentType: e.target.value }))}
          >
            <option value="">All Content Types</option>
            <option value="video_embed">Video Embed</option>
            <option value="video_link">Video Link</option>
            <option value="pdf">PDF</option>
            <option value="html5">HTML5</option>
            <option value="scorm">SCORM</option>
            <option value="document">Document</option>
            <option value="interactive">Interactive</option>
          </select>
          
          <select 
            value={filters.difficulty}
            onChange={(e) => setFilters(prev => ({ ...prev, difficulty: e.target.value }))}
          >
            <option value="">All Difficulty Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>
          
          <input 
            type="text"
            placeholder="Filter by tag value..."
            value={filters.tagValue}
            onChange={(e) => setFilters(prev => ({ ...prev, tagValue: e.target.value }))}
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="content-area">
        {activeTab === 'modules' && (
          <ModulesTab 
            modules={modules}
            loading={loading}
            onEdit={handleEditModule}
            onDelete={handleDeleteModule}
            getContentTypeIcon={getContentTypeIcon}
            userRole={userRole}
            onStartModule={handleStartModule}
          />
        )}
        
        {activeTab === 'paths' && (
          <LearningPathsTab 
            paths={learningPaths}
            loading={loading}
            onEdit={handleEditPath}
            onDelete={handleDeletePath}
            userRole={userRole}
          />
        )}
        
        {activeTab === 'progress' && (
          <ProgressTab 
            progress={userProgress}
            modules={modules}
            loading={loading}
          />
        )}
      </div>

      {/* Admin/Change Manager only modals */}
      {canManage && (
        <>
          {/* Module Modal */}
          {showModuleModal && (
            <ModuleModal 
              module={editingModule}
              form={moduleForm}
              setForm={setModuleForm}
              onSave={handleSaveModule}
              onClose={() => setShowModuleModal(false)}
              onUpload={() => setShowUploadModal(true)}
              addTag={addTag}
              updateTag={updateTag}
              removeTag={removeTag}
              modules={modules}
            />
          )}

          {/* Learning Path Modal */}
          {showPathModal && (
            <LearningPathModal 
              path={editingPath}
              form={pathForm}
              setForm={setPathForm}
              onSave={handleSavePath}
              onClose={() => setShowPathModal(false)}
              modules={modules}
            />
          )}

          {/* Upload Modal */}
          {showUploadModal && (
            <UploadModal 
              onUpload={handleFileUpload}
              onClose={() => setShowUploadModal(false)}
            />
          )}
        </>
      )}

      {/* SCORM Viewer */}
      <ScormViewer
        module={scormModule}
        isOpen={showScormViewer}
        onClose={() => {
          setShowScormViewer(false);
          setScormModule(null);
        }}
        onComplete={handleScormComplete}
      />
    </div>
  );
};

// Modules Tab Component
const ModulesTab = ({ modules, loading, onEdit, onDelete, getContentTypeIcon, userRole, onStartModule }) => {
  if (loading) return <div className="loading">Loading modules...</div>;

  const isAdmin = userRole === 'Admin' || userRole === 'System Administrator';
  const isChangeManager = userRole === 'Change Manager';
  const canManage = isAdmin || isChangeManager;

  return (
    <div className="modules-grid">
      {modules.map(module => (
        <div key={module.id} className="module-card">
          <div className="module-header">
            <div className="module-icon">
              {getContentTypeIcon(module.content_type)}
            </div>
            <div className="module-actions">
              {canManage ? (
                // Admin/Change Manager View - Both management AND learner buttons
                <div className="admin-actions">
                  <div className="management-buttons">
                    <button onClick={() => onEdit(module)} className="btn-icon" title="Edit Module">
                      <FaEdit />
                    </button>
                    <button onClick={() => onDelete(module.id)} className="btn-icon delete" title="Delete Module">
                      <FaTrash />
                    </button>
                  </div>
                  <button 
                    onClick={() => onStartModule(module)} 
                    className="btn-primary start-module"
                    title="Take This Training"
                  >
                    <FaPlay /> Take Training
                  </button>
                </div>
              ) : (
                // Regular User View - Start Module button only
                <button 
                  onClick={() => onStartModule(module)} 
                  className="btn-primary start-module"
                  title="Start Training Module"
                >
                  <FaPlay /> Start Module
                </button>
              )}
            </div>
          </div>
          
          <div className="module-content">
            <h3>{module.title}</h3>
            <p>{module.description}</p>
            
            <div className="module-meta">
              <span className={`difficulty ${module.difficulty_level}`}>
                {module.difficulty_level}
              </span>
              {module.duration_minutes && (
                <span className="duration">
                  <FaClock /> {module.duration_minutes}m
                </span>
              )}
              {module.is_mandatory && (
                <span className="mandatory">Required</span>
              )}
            </div>
            
            <div className="module-tags">
              {module.tags?.map((tag, index) => (
                <span key={index} className="tag">
                  {tag.tag_type}: {tag.tag_value}
                </span>
              ))}
            </div>

            {/* Show progress for everyone who takes training */}
            {module.user_progress && (
              <div className="learner-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${module.user_progress.progress_percentage || 0}%` }}
                  />
                </div>
                <span className="progress-text">
                  {module.user_progress.completed_at ? 
                    '✓ Completed' : 
                    `${module.user_progress.progress_percentage || 0}% Complete`
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Learning Paths Tab Component
const LearningPathsTab = ({ paths, loading, onEdit, onDelete, userRole }) => {
  if (loading) return <div className="loading">Loading learning paths...</div>;

  const isAdmin = userRole === 'Admin' || userRole === 'System Administrator';
  const isChangeManager = userRole === 'Change Manager';
  const canManage = isAdmin || isChangeManager;

  return (
    <div className="paths-list">
      {paths.map(path => (
        <div key={path.id} className="path-card">
          <div className="path-header">
            <h3>{path.name}</h3>
            <div className="path-actions">
              {canManage && (
                <>
                  <button onClick={() => onEdit(path)} className="btn-icon">
                    <FaEdit />
                  </button>
                  <button onClick={() => onDelete(path.id)} className="btn-icon delete">
                    <FaTrash />
                  </button>
                </>
              )}
            </div>
          </div>
          
          <p>{path.description}</p>
          
          <div className="path-meta">
            {path.target_role && <span>Role: {path.target_role}</span>}
            {path.target_department && <span>Dept: {path.target_department}</span>}
            {path.change_impact_level && <span>Impact: {path.change_impact_level}</span>}
            {path.estimated_duration_hours && (
              <span>
                <FaClock /> {path.estimated_duration_hours}h
              </span>
            )}
          </div>
          
          <div className="path-modules">
            <strong>Modules ({path.items?.length || 0}):</strong>
            {path.items?.map((item, index) => (
              <span key={index} className="module-item">
                {item.module?.title}
                {item.is_optional && <em> (optional)</em>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Progress Tab Component
const ProgressTab = ({ progress, modules, loading }) => {
  if (loading) return <div className="loading">Loading progress...</div>;

  const getModuleTitle = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module?.title || 'Unknown Module';
  };

  return (
    <div className="progress-overview">
      <div className="progress-stats">
        <div className="stat-card">
          <h3>Completed Modules</h3>
          <div className="stat-value">
            {progress.filter(p => p.completed_at).length}
          </div>
        </div>
        <div className="stat-card">
          <h3>In Progress</h3>
          <div className="stat-value">
            {progress.filter(p => p.started_at && !p.completed_at).length}
          </div>
        </div>
        <div className="stat-card">
          <h3>Total Time</h3>
          <div className="stat-value">
            {Math.round(progress.reduce((sum, p) => sum + (p.time_spent_minutes || 0), 0) / 60)}h
          </div>
        </div>
      </div>
      
      <div className="progress-list">
        {progress.map(completion => (
          <div key={completion.id} className="progress-item">
            <div className="progress-info">
              <h4>{getModuleTitle(completion.module_id)}</h4>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${completion.progress_percentage}%` }}
                />
              </div>
              <span>{completion.progress_percentage}%</span>
            </div>
            
            <div className="progress-meta">
              {completion.completed_at && (
                <span className="completed">
                  ✓ Completed {new Date(completion.completed_at).toLocaleDateString()}
                </span>
              )}
              {completion.time_spent_minutes && (
                <span>Time: {completion.time_spent_minutes}m</span>
              )}
              {completion.score_percentage && (
                <span>Score: {completion.score_percentage}%</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Module Modal Component
const ModuleModal = ({ 
  module, 
  form, 
  setForm, 
  onSave, 
  onClose, 
  onUpload, 
  addTag, 
  updateTag, 
  removeTag, 
  modules 
}) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{module ? 'Edit Module' : 'Create New Module'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Module title"
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Module description"
              rows="3"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Content Type</label>
              <select
                value={form.content_type}
                onChange={e => setForm(prev => ({ ...prev, content_type: e.target.value }))}
              >
                <option value="video_embed">Video Embed</option>
                <option value="video_link">Video Link</option>
                <option value="pdf">PDF</option>
                <option value="html5">HTML5</option>
                <option value="scorm">SCORM</option>
                <option value="document">Document</option>
                <option value="interactive">Interactive</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Difficulty Level</label>
              <select
                value={form.difficulty_level}
                onChange={e => setForm(prev => ({ ...prev, difficulty_level: e.target.value }))}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label>Content URL</label>
            <input
              type="url"
              value={form.content_url}
              onChange={e => setForm(prev => ({ ...prev, content_url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                value={form.duration_minutes}
                onChange={e => setForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
                min="0"
              />
            </div>
            
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_mandatory}
                  onChange={e => setForm(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                />
                Mandatory Module
              </label>
            </div>
          </div>
          
          <div className="form-group">
            <label>Tags</label>
            <div className="tags-container">
              {form.tags.map((tag, index) => (
                <div key={index} className="tag-row">
                  <select
                    value={tag.tag_type}
                    onChange={e => updateTag(index, 'tag_type', e.target.value)}
                  >
                    <option value="project">Project</option>
                    <option value="department">Department</option>
                    <option value="role">Role</option>
                    <option value="skill">Skill</option>
                  </select>
                  <input
                    type="text"
                    value={tag.tag_value}
                    onChange={e => updateTag(index, 'tag_value', e.target.value)}
                    placeholder="Tag value"
                  />
                  <button type="button" onClick={() => removeTag(index)} className="btn-remove">
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addTag} className="btn-add-tag">
                <FaPlus /> Add Tag
              </button>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={onUpload} className="btn-upload">
              <FaUpload /> Upload File
            </button>
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={onSave} className="btn-primary">
            {module ? 'Update' : 'Create'} Module
          </button>
        </div>
      </div>
    </div>
  );
};

// Learning Path Modal Component
const LearningPathModal = ({ 
  path, 
  form, 
  setForm, 
  onSave, 
  onClose, 
  modules 
}) => {
  const addModule = () => {
    setForm(prev => ({
      ...prev,
      modules: [...prev.modules, { module_id: '', is_optional: false }]
    }));
  };

  const updateModule = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.map((mod, i) => 
        i === index ? { ...mod, [field]: value } : mod
      )
    }));
  };

  const removeModule = (index) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{path ? 'Edit Learning Path' : 'Create New Learning Path'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Learning path name"
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Learning path description"
              rows="3"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Target Role</label>
              <input
                type="text"
                value={form.target_role}
                onChange={e => setForm(prev => ({ ...prev, target_role: e.target.value }))}
                placeholder="Target role"
              />
            </div>
            
            <div className="form-group">
              <label>Target Department</label>
              <input
                type="text"
                value={form.target_department}
                onChange={e => setForm(prev => ({ ...prev, target_department: e.target.value }))}
                placeholder="Target department"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Change Impact Level</label>
              <select
                value={form.change_impact_level}
                onChange={e => setForm(prev => ({ ...prev, change_impact_level: e.target.value }))}
              >
                <option value="">Select impact level</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Estimated Duration (hours)</label>
              <input
                type="number"
                value={form.estimated_duration_hours}
                onChange={e => setForm(prev => ({ ...prev, estimated_duration_hours: e.target.value }))}
                min="0"
                step="0.5"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Learning Path Modules</label>
            <div className="modules-container">
              {form.modules.map((moduleItem, index) => (
                <div key={index} className="module-row">
                  <select
                    value={moduleItem.module_id}
                    onChange={e => updateModule(index, 'module_id', e.target.value)}
                  >
                    <option value="">Select module</option>
                    {modules.map(module => (
                      <option key={module.id} value={module.id}>
                        {module.title}
                      </option>
                    ))}
                  </select>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={moduleItem.is_optional}
                      onChange={e => updateModule(index, 'is_optional', e.target.checked)}
                    />
                    Optional
                  </label>
                  <button type="button" onClick={() => removeModule(index)} className="btn-remove">
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addModule} className="btn-add-module">
                <FaPlus /> Add Module
              </button>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={onSave} className="btn-primary">
            {path ? 'Update' : 'Create'} Learning Path
          </button>
        </div>
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ onUpload, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      await onUpload(selectedFile);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Training File</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="upload-area">
            <input
              type="file"
              id="file-upload"
              onChange={handleFileSelect}
              accept=".pdf,.mp4,.zip,.html,.scorm"
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload" className="upload-label">
              <FaUpload className="upload-icon" />
              <div>Click to select file or drag and drop</div>
              <div className="upload-hint">
                Supported: PDF, MP4, ZIP, HTML, SCORM (Max: 500MB)
              </div>
            </label>
          </div>
          
          {selectedFile && (
            <div className="selected-file">
              <div className="file-info">
                <strong>{selectedFile.name}</strong>
                <span>{formatFileSize(selectedFile.size)}</span>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button 
            onClick={handleUpload} 
            className="btn-primary"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Training; 